import React from 'react'
import styled, { css } from 'styled-components'

import { baseInputCss } from '@blade/ui-kit'
import { TextareaAutosize } from '@blade/ui-kit'
import { PlatformUtil } from '@root/shared/core'

import { AnimateHeight } from '../../animations'
import {
  FONT_SIZE_MAP,
  FONT_WEIGHT_MAP,
  HEIGHT_MAP,
  RADIUS_MAP,
} from '../constants'

import { TextareaAttachments } from './textarea-attachments'
import { TextareaCounter } from './textarea-counter'
import { TextareaEmojis } from './textarea-emojis'

import type { Size } from '../input-size'
import type { TypeHelper } from '@blade/std-lib'

export type TextareaProps = {
  label?: string
  required?: boolean
  size?: Size
  errorMessage?: string
  emojiPicker?: boolean
  symbolCount?: boolean
  attachments?: Array<string> | undefined
  onAttachmentsAdd?: (attachments: Array<string>) => void
  onAttachmentsRemove?: (index: number) => void
  onChange?: (value: string) => void
  value?: string | number
  ref?: React.Ref<HTMLTextAreaElement>
} & TypeHelper.OmitKey<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  'onChange' | 'value'
>

const Textarea_ = ({
  size = 's',
  errorMessage,
  className,
  label,
  required,
  maxLength,
  onChange,
  onAttachmentsAdd,
  onAttachmentsRemove,
  emojiPicker = false,
  symbolCount = false,
  attachments = undefined,
  ref,
  value,
  ...props
}: TextareaProps) => {
  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const next = event.target.value
    onChange?.(next)
  }

  const handleAttachmentsAdd = (attachments: Array<string>) =>
    onAttachmentsAdd?.(attachments)
  const handleAttachmentsRemove = (index: number) =>
    onAttachmentsRemove?.(index)

  const renderTools = () => {
    const hostPlatform = PlatformUtil.hostPlatform()
    const showEmoji =
      hostPlatform === undefined
        ? emojiPicker
        : !['android', 'ios'].includes(hostPlatform)
    const hasTools =
      maxLength !== undefined ||
      symbolCount ||
      emojiPicker ||
      attachments !== undefined

    if (!hasTools) {
      return undefined
    }

    return (
      <InputTools>
        <StartContent>
          {showEmoji ? (
            <TextareaEmojis
              value={value?.toString() ?? ''}
              onChange={handleChange}
            />
          ) : undefined}
          {attachments ? (
            <TextareaAttachments.AttachmentsInput
              onAttachmentsAdd={handleAttachmentsAdd}
            />
          ) : undefined}
        </StartContent>
        {symbolCount ? (
          <TextareaCounter value={value} maxLength={maxLength} />
        ) : undefined}
      </InputTools>
    )
  }

  return (
    <Root className={className} data-disabled={props.disabled}>
      <label>
        {label ? (
          <Label>
            {label}
            {required ? <Required>*</Required> : undefined}
          </Label>
        ) : undefined}
        <RootInput $size={size} data-error={!!errorMessage}>
          <TextareaInput
            $size={size}
            ref={ref}
            maxLength={maxLength}
            value={value}
            onChange={handleChange}
            {...props}
            $withAttachments={
              attachments !== undefined && attachments.length > 0
            }
          />
          <Footer>
            <TextareaAttachments
              attachments={attachments}
              onAttachmentsRemove={handleAttachmentsRemove}
            />
            {renderTools()}
          </Footer>
        </RootInput>
      </label>
      <AnimateHeight>
        {errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : undefined}
      </AnimateHeight>
    </Root>
  )
}

export const Textarea = Textarea_ as (props: TextareaProps) => React.ReactNode

const Root = styled.div``
const Required = styled.span`
  color: var(--error-9);
  font-weight: 600;
  font-size: 14px;
`

const Label = styled.div`
  font-size: 12px;
  color: var(--text-11);
  margin-bottom: 8px;
`

const InputTools = styled.div`
  color: var(--text-6);

  font-size: 11px;
  display: flex;
`

const StartContent = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 7px;
`

const RootInput = styled.div<{ $size: Size }>`
  padding: var(--input-padding-inline, 12px);
  border-radius: ${({ $size }) => RADIUS_MAP[$size]}px;

  background: var(--appearanceA-7);
  position: relative;
  border: 0.5px solid transparent;

  label {
    position: relative;
  }
  &:focus-within {
    background: var(--appearance-1);
    border: 0.5px solid var(--backgroundA-4);
  }
  &[data-error='true'] {
    border: 0.5px solid var(--errorA-6);
  }
`

const TextareaInput = styled(TextareaAutosize)<{
  $size: Size
  $withAttachments: boolean
}>`
  ${baseInputCss}
  --input-height: ${({ $size }) => HEIGHT_MAP[$size]}px;
  font-size: ${({ $size }) => FONT_SIZE_MAP[$size]}px;
  font-weight: ${({ $size }) => FONT_WEIGHT_MAP[$size]};

  resize: vertical;

  border: 0.5px solid transparent;
  display: block;
  width: 100%;
  max-width: 100%;
  line-height: 16px;

  min-height: var(--input-height);

  transition: border-color 300ms ease;
  resize: none;
  font-weight: 500;

  ${({ $withAttachments }) => $withAttachments && css``}
`

const ErrorMessage = styled.div`
  color: var(--error-9);

  font-size: 10px;
  font-weight: 500;
  line-height: 16px;
`

const Footer = styled.div`
  display: grid;
  padding-top: 10px;
  gap: 7px;
`
